import {
  BuildingOffice2Icon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, type FieldErrors } from 'react-hook-form';
import type { ActorRefFrom } from 'xstate';
import { Button, Tabs, TextArea, TextInput } from '~/components';
import { TermCompareSection } from '~/features/market/negotiations/sub-components/TermCompareSection';
import { JOB_NEGOTIATION_TERM_KEYS } from '~/features/market/negotiations/utils';
import { useCustomForm } from '~/hooks/useCustomForm';
import type { ServiceOpportunity } from './schemas';
import type { serviceConversationMachine } from './serviceConversation.machine';
import {
  counterWindowSchema,
  serviceCounterFormSchema,
  type ServiceCounterDraft,
} from './serviceCounterDraft';
import {
  AVAILABILITY_TAB,
  isRequiredTerm,
  previousTermText,
  SERVICE_NEGOTIATION_TAB_NAMES,
  SERVICE_TERM_TABS,
  serviceTermLabel,
  tabIndexForField,
  termChanged,
  TermField,
  termValue,
  type ServiceTerm,
} from './serviceNegotiationTerms';
import { canCounterServiceOffer } from './serviceOfferActions';
import { ServiceOfferSummary } from './ServiceOfferSummary';

type Props = {
  actor: ActorRefFrom<typeof serviceConversationMachine>;
  draft: ServiceCounterDraft;
  opportunity: ServiceOpportunity;
  disabled: boolean;
  loading: boolean;
  error: string | null;
  errorRevision: number;
  availabilityError: string | null;
  onClose: () => void;
};

/** The service counter, laid out like the job negotiation: previous terms beside proposed ones. */
export const ServiceCounterEditor = ({
  actor,
  draft,
  opportunity,
  disabled,
  loading,
  error,
  errorRevision,
  availabilityError,
  onClose,
}: Props) => {
  const [tabIndex, setTabIndex] = useState(0);
  const editorRef = useRef<HTMLElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const reviewRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!draft.focusOnOpen) return;
    editorRef.current?.focus({ preventScroll: true });
    actor.send({ type: 'COUNTER_FOCUSED' });
  }, [actor, draft.focusOnOpen]);
  useEffect(() => {
    if (!error) return;
    errorRef.current?.scrollIntoView({ block: 'nearest' });
    errorRef.current?.focus({ preventScroll: true });
  }, [error, errorRevision]);
  useEffect(() => {
    if (draft.reviewed) {
      reviewRef.current?.scrollIntoView({ block: 'start' });
      reviewRef.current?.focus({ preventScroll: true });
    }
  }, [draft.reviewed]);
  const methods = useCustomForm<Record<string, string>>({
    resolver: zodResolver(serviceCounterFormSchema),
    resetOptions: { keepErrors: true, keepIsSubmitted: true },
    values: {
      ...Object.fromEntries(
        Object.entries(draft.terms).map(([key, value]) => [key, value == null ? '' : String(value)])
      ),
      messageBody: draft.messageBody,
      windowStart: draft.window.start,
      windowEnd: draft.window.end,
    },
  });
  const current =
    canCounterServiceOffer(opportunity) &&
    opportunity.latestNegotiation?.id === draft.expectedNegotiationId;
  const previousTerms = opportunity.latestNegotiation?.terms ?? {};
  const changed = (term: ServiceTerm) => termChanged(term, draft.terms, previousTerms);
  const changedCount = JOB_NEGOTIATION_TERM_KEYS.filter(changed).length;
  const selected = draft.entries.filter((entry) =>
    draft.selectedIds.includes(entry.availabilityEntryId)
  );
  const preview =
    opportunity.latestNegotiation && draft.reviewed
      ? {
          ...opportunity.latestNegotiation,
          terms: draft.reviewed.negotiationTerms,
          availabilitySelection: selected,
          selectionStatus: 'RECORDED' as const,
        }
      : null;
  const viewerIsResponder = opportunity.viewerRole === 'RESPONDER';
  const counterparty = opportunity.counterparty.displayName;
  const windowValid = counterWindowSchema.safeParse(draft.window).success;
  // Only the active tab is mounted, so an error elsewhere must bring its tab forward.
  const showFirstError = (errors: FieldErrors) => {
    const fields = Object.keys(errors);
    if (fields.some((field) => tabIndexForField(field) === tabIndex)) return;
    const target = fields.map(tabIndexForField).find((index) => index >= 0);
    if (target !== undefined) setTabIndex(target);
  };
  const tabs = SERVICE_NEGOTIATION_TAB_NAMES.map((name, index) => ({
    name,
    current: index === tabIndex,
    cyTag: `service-negotiation-tab-${name.toLowerCase().replace(/\s+/g, '-')}`,
  }));
  const termTab = SERVICE_TERM_TABS.find((tab) => tab.name === tabs[tabIndex].name);

  return (
    <FormProvider {...methods}>
      <section
        ref={editorRef}
        tabIndex={-1}
        aria-label="Counteroffer editor"
        className="mb-4 max-w-[calc(100vw-4rem)] sm:max-w-none"
      >
        <div className="my-6 text-left">
          <div className="flex flex-row justify-between gap-3">
            <h2 className="sub-heading-sb mb-3">Service Contract Negotiation</h2>
            <span
              className="inline-flex items-center self-start rounded-full bg-warning-500 px-3 py-1 text-sm font-medium text-white"
              data-cy="service-negotiation-version-status"
            >
              Version {draft.version} • {current ? 'Your Turn' : 'Review Terms'}
            </span>
          </div>
          <p className="body-text">{opportunity.listing.title}</p>
        </div>
        <div className="mb-4 flex flex-row justify-between gap-2 rounded-lg bg-grey-900 p-4">
          <div className="flex flex-row gap-2">
            <UserIcon className="my-auto h-5 w-5 text-grey-100" />
            <div className="body-text text-left">
              <p className="pb-2 text-grey-400">Listing Provider</p>
              <p>{viewerIsResponder ? counterparty : 'You'}</p>
            </div>
          </div>
          <ChevronRightIcon className="my-auto h-5 w-5 text-grey-100" />
          <div className="flex flex-row gap-2">
            <div className="body-text text-left">
              <p className="pb-2 text-grey-400">Responding Employer</p>
              <p>{viewerIsResponder ? 'You' : counterparty}</p>
            </div>
            <BuildingOffice2Icon className="my-auto h-5 w-5 text-grey-100" />
          </div>
        </div>
        <div className="mb-4 flex flex-row">
          <InformationCircleIcon className="my-auto h-5 w-5 text-warning-500" />
          <p className="body-text my-auto ml-2 text-left text-warning-500">
            {changedCount} {changedCount === 1 ? 'field' : 'fields'} modified
          </p>
          <p className="body-text my-auto ml-2 text-left">from previous terms</p>
        </div>
        <p className="mb-4 text-left text-sm">
          Your draft is kept while you move around the workspace. Closing this window discards it.
          Opening, editing, selecting dates or closing does not send an offer or change holds.
          Change the rate for your compensation type (hour, shift or RVU). The server calculates
          derived rates and totals from these inputs; the old offer's calculated totals are not
          proposed counter totals. All supplied contract text is screened while identities are
          masked.
        </p>
        {!current && (
          <p role="status" className="mb-4 text-left">
            The current offer or your permissions changed. Your original draft is preserved. Close
            this window to discard it and review the current offer before starting a new counter.
          </p>
        )}
        <form
          onSubmit={methods.handleSubmit(
            () => actor.send({ type: 'REVIEW_COUNTER' }),
            showFirstError
          )}
          onChange={(event) => {
            const input = event.target;
            if (
              !(
                input instanceof HTMLInputElement ||
                input instanceof HTMLTextAreaElement ||
                input instanceof HTMLSelectElement
              )
            )
              return;
            if (input.name === 'messageBody')
              actor.send({ type: 'EDIT_COUNTER', messageBody: input.value });
            else if (input.name === 'windowStart' || input.name === 'windowEnd')
              actor.send({
                type: 'COUNTER_WINDOW',
                window: {
                  ...draft.window,
                  [input.name === 'windowStart' ? 'start' : 'end']: input.value,
                },
              });
            else if (JOB_NEGOTIATION_TERM_KEYS.some((key) => key === input.name)) {
              const key = input.name as ServiceTerm;
              actor.send({ type: 'EDIT_COUNTER', terms: { [key]: termValue(key, input.value) } });
            }
          }}
          className="space-y-4"
        >
          <Tabs tabs={tabs} activeTab={tabIndex} handleTabChange={setTabIndex} />
          {Object.keys(methods.formState.errors).length > 0 && (
            <div className="my-4 flex flex-row gap-2 rounded-lg bg-error-t-900 p-4">
              <ExclamationCircleIcon className="my-auto h-5 w-5 text-error" />
              <p className="body-text my-auto text-left text-error">
                There are form errors. Please fix them before proceeding.
              </p>
            </div>
          )}
          <fieldset disabled={disabled || !current} className="m-0 min-w-0 space-y-4 border-0 p-0">
            <div data-cy="service-negotiation-tab-content" className="min-w-0 overflow-x-auto">
              {termTab ? (
                <div
                  className={
                    termTab.sections.length > 1
                      ? 'grid grid-cols-1 gap-4 lg:grid-cols-2'
                      : 'grid grid-cols-1 gap-4'
                  }
                >
                  {termTab.sections.map((section) => (
                    <TermCompareSection
                      key={section.title}
                      title={section.title}
                      changedTermsCount={section.terms.filter(changed).length}
                      terms={section.terms.map((term) => ({
                        label: `${serviceTermLabel(term)}${isRequiredTerm(term) ? ' *' : ''}`,
                        value: previousTermText(term, previousTerms[term]),
                        newValueField: <TermField term={term} flagged={changed(term)} />,
                      }))}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="space-y-4 text-left"
                  data-cy={`service-negotiation-${AVAILABILITY_TAB.toLowerCase()}`}
                >
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <TextInput name="windowStart" label="Availability window start" type="date" />
                    <TextInput name="windowEnd" label="Availability window end" type="date" />
                  </div>
                  {!windowValid && (
                    <p role="status">Choose a valid availability window of at most 366 days.</p>
                  )}
                  <Button
                    type="button"
                    label="Load counter availability"
                    disabled={!windowValid}
                    onClick={() => actor.send({ type: 'LOAD_COUNTER_AVAILABILITY' })}
                  />
                  {draft.selectionLoaded && draft.entries.length === 0 && (
                    <p role="status">
                      No current availability in this window. Choose another window or refresh the
                      response.
                    </p>
                  )}
                  {draft.entries.map((entry) => (
                    <label
                      key={entry.availabilityEntryId}
                      className="block space-y-1 rounded border p-3"
                    >
                      <span className="flex gap-2">
                        <input
                          type="checkbox"
                          aria-label={`Include ${entry.date} (${entry.availabilityEntryId})`}
                          checked={draft.selectedIds.includes(entry.availabilityEntryId)}
                          onChange={() =>
                            actor.send({
                              type: 'TOGGLE_COUNTER_ENTRY',
                              entryId: entry.availabilityEntryId,
                            })
                          }
                        />
                        Include {entry.date} ({entry.availabilityEntryId})
                      </span>
                      <span className="block break-words text-sm">
                        {entry.detailMode === 'DATE_ONLY'
                          ? 'Date only — no shift times agreed'
                          : `${entry.startsAt} – ${entry.endsAt}`}{' '}
                        · {entry.timezone} · {entry.shiftLabel ?? 'No shift label'} ·{' '}
                        {entry.shiftType ?? 'No shift type'} ·{' '}
                        {entry.workLocation ?? 'No work location'} · {entry.stateCode ?? 'No state'}
                      </span>
                      <span className="block text-sm">
                        Rate override: {entry.rateOverrideType ?? 'None'}; hourly{' '}
                        {entry.pricePerHourOverride ?? 'none'}; shift{' '}
                        {entry.pricePerShiftOverride ?? 'none'}; call shift{' '}
                        {entry.pricePerCallShiftOverride ?? 'none'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <TextArea name="messageBody" label="Counteroffer message" required rows={3} />
          </fieldset>
          {availabilityError && (
            <p role="alert" className="text-left text-sm text-danger">
              {availabilityError}
            </p>
          )}
          {loading && <p role="status">Loading counter availability…</p>}
          {error && (
            <p ref={errorRef} tabIndex={-1} role="alert" className="text-left text-sm text-danger">
              {error}
            </p>
          )}
          {preview && current && (
            <section
              ref={reviewRef}
              tabIndex={-1}
              aria-label="Review counteroffer"
              className="space-y-4 rounded border p-3 text-left"
            >
              <h3 className="body-text-sb">Review counteroffer</h3>
              <ServiceOfferSummary offer={preview} proposed />
              <p className="whitespace-pre-wrap break-words">{draft.reviewed?.messageBody}</p>
              <p>
                This submits a new offer on this response and updates its selected availability
                holds. It resets acceptance of the previous offer. It does not accept an offer,
                share identities or finalize a booking or contract. Both parties must accept the new
                canonical offer for agreement.
              </p>
            </section>
          )}
          <div className="mt-6 flex gap-4 border-t-2 border-grey-900 pt-5">
            <Button
              type="button"
              colorVariant="tertiary"
              label="Close"
              disabled={disabled}
              onClick={onClose}
              cyTag="service-negotiation-close"
            />
            <div className="ml-auto flex gap-4">
              {preview && current ? (
                <Button
                  type="button"
                  label={`Submit counteroffer to version ${draft.version}`}
                  disabled={disabled}
                  onClick={() => actor.send({ type: 'COUNTER' })}
                />
              ) : (
                <Button
                  type="submit"
                  label="Review counteroffer"
                  disabled={
                    disabled || !current || !draft.selectionLoaded || draft.selectedIds.length === 0
                  }
                />
              )}
            </div>
          </div>
        </form>
      </section>
    </FormProvider>
  );
};
